import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Contact } from '@hero/schema';
import { SharedTable, type StatusBadgeConfigType } from '../../../app/Table/SharedTable';
import type { SearchInputProps } from '../../../app/Search/SearchInput';
import { ContactRow } from './ContactRow';

export interface ContactStatusFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: Array<{
    value: string;
    label: string;
    count?: number;
    icon?: React.ReactNode;
    className?: string;
  }>;
  counts?: Array<{ value: string; count: number }>;
}

export interface ContactTableFilters {
  showSearch?: boolean;
  statusFilter?: ContactStatusFilterConfig;
}

export interface ContactTableProps {
  contacts: Contact[];
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  filters?: ContactTableFilters;
  /** Empty-state shown only when the list is empty AND no filters/search are active. */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
  children?: React.ReactNode;
}

const CONTACT_STATUS_BADGE_CONFIG: StatusBadgeConfigType = {
  getStatusClass: (type: string) => {
    switch (type) {
      case 'own':
        return 'bg-primary-100 text-logoBlue';
      case 'other':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  },
  getStatusDisplayText: (status: string) => status,
};

const ContactTableComponent: React.FC<ContactTableProps> = ({
  contacts,
  isLoading,
  sortColumn,
  sortDirection = 'asc',
  searchProps,
  onSort,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  filters,
  emptyState,
  hasActiveFilters,
  children,
}) => {
  const { t } = useTranslation(['m-contact']);

  const columns = useMemo(
    () => [
      { key: 'name', label: t('m-contact:table.name', 'Name') },
      { key: 'phone', label: t('m-contact:table.phone', 'Phone') },
      { key: 'type', label: t('m-contact:table.type', 'Type') },
      { key: 'actions', label: '', sortable: false },
    ],
    [t],
  );

  return (
    <SharedTable
      data={contacts}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={CONTACT_STATUS_BADGE_CONFIG}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      hasActiveFilters={hasActiveFilters}
      getRowId={(contact: Contact) => contact._id.toString()}>
      {children || <ContactTableHead filters={filters} />}

      <SharedTable.Mid>
        <SharedTable.Rows
          columns={columns}
          rowRender={(contact: Contact) => <ContactRow contact={contact} />}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

const ContactTableHead: React.FC<{ filters?: ContactTableFilters }> = ({ filters }) => {
  if (!filters) return null;

  return (
    <SharedTable.Head>
      {filters.showSearch !== false && <SharedTable.Search />}
      {filters.statusFilter && (
        <SharedTable.Badge
          options={filters.statusFilter.options}
          counts={filters.statusFilter.counts}
          value={filters.statusFilter.value}
          onChange={filters.statusFilter.onChange}
          showCounts={!!filters.statusFilter.counts}
        />
      )}
    </SharedTable.Head>
  );
};

export const ContactTable = Object.assign(ContactTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});
